/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

document.addEventListener("DOMContentLoaded", () => {
  const wrapper = document.querySelector(".detail.product-column-right");
 
  if (!wrapper) {
      return;
  }
  const productTag = wrapper.getAttribute("data-product-tag");
  console.log("productTag",productTag);
  
  const savedOption = localStorage.getItem(productTag);
  if (!savedOption) return;

  console.log("Restoring:", productTag, "=>", savedOption);

  // Find the radio that controls option1
  const radios = document.querySelectorAll('input[type="radio"].opt-btn');

  radios.forEach(radio => {
    if (radio.value === savedOption) {
      radio.checked = true;

      radio.dispatchEvent(new Event("change", { bubbles: true }));
    }
  });
});

  var hotspot_title_desktop = document.querySelectorAll(".desktop-only .focal_point"); 
  hotspot_title_desktop.forEach(function(data_title) {
    data_title.addEventListener("click", (e) => { 
        e.preventDefault();
        var tab_inne = document.querySelectorAll(".desktop-only .tab_inner_sec .product-grid-block");  
        hotspot_title_desktop.forEach(el => el.classList.remove("is_active"));
        tab_inne.forEach(el => el.classList.remove("is_active"));  
        data_title.classList.add("is_active");
      var targetIndex = data_title.closest('.shop-the-look-swiper').querySelector('.desktop-only [data-product-title="'+ data_title.dataset.title +'"]');          
         if(targetIndex){
            targetIndex.classList.add("is_active")
         }
    });
  });
  var hotspot_title_mobile = document.querySelectorAll(".mobile-only .focal_point"); 
  hotspot_title_mobile.forEach(function(data_title) {
    data_title.addEventListener("click", (e) => { 
        e.preventDefault();
        var tab_inne = document.querySelectorAll(".mobile-only .focal_selected .product-grid-block");  
        var tab_inne_txt = document.querySelectorAll(".mobile-only .tab_inner_sec .product-grid-block");  
        hotspot_title_mobile.forEach(el => el.classList.remove("is_active"));
        tab_inne.forEach(el => el.classList.remove("is_active"));  
        tab_inne_txt.forEach(el => el.classList.remove("is_active")); 
        data_title.classList.add("is_active");
      var targetIndex_img = data_title.closest('.mobile-only ').querySelector('[data-product-title="'+ data_title.dataset.title +'"]'); 
      var targetIndex_txt = data_title.closest('.mobile-only ').querySelector('.tab_inner_sec [data-product-title="'+ data_title.dataset.title +'"]'); 
      console.log("targetIndex_txt", targetIndex_txt)         
         if(targetIndex_img){
            targetIndex_img.classList.add("is_active")
         }
         if(targetIndex_txt){
            targetIndex_txt.classList.add("is_active")
         }
    });
  });
// ================PDP gallery popup open ==========
document.querySelectorAll(".image-gallery-wrapper .gallery-url").forEach(function (trigger) {
    trigger.addEventListener("click", function (e) {
      e.preventDefault();
      // console.log("This",this.querySelector("video"))
      if(this.querySelector("video")){
        this.querySelector("video").pause();
      }      
      var media_id = this.closest(".image-gallery-item-inner").getAttribute("data-media-id");
      // Close all popups first
      document.querySelectorAll(".product-csutom-data-popup  .video_popup").forEach(function (popup) {
        popup.classList.remove("active");
        const video = popup.querySelector("video");
        if (video) {
          video.pause();
          video.currentTime = 0;
        }
      });
      // Show current popup
      const popup = document.querySelector(`[data-popup-media-id="${media_id}"]`)?.querySelector(".video_popup");
      // console.log("popup", popup)
      if (popup) {
        popup.classList.add("active");
        const video = popup.querySelector("video");
        // console.log("video", video)
        if (video) {
          video.play();          
        }
      }
    });
  });
// ===============  PDP gallery popup  Close =======================
  document.querySelectorAll(".video_close_icon").forEach(function (closeBtn) {
    closeBtn.addEventListener("click", function () {
      const main_wrp = this.closest(".image-gallery-item-inner");
      const popup = main_wrp.querySelector(".video_popup");
      popup.classList.remove("active");
      const video = popup.querySelector("video");
      if (video) {
        video.pause();
        video.currentTime = 0;
      }
    });
  });
// =================== Overlay close POPUP =====
document.querySelectorAll(".product-csutom-data-popup").forEach(function (overlay) {
  overlay.addEventListener("click", function (e) {
    // Ensure the user clicked on the overlay itself, not the inner content
    if (e.target === overlay) {
         const active_close =  document.querySelector(".video_popup.active").closest(".image-gallery-item-inner")
         if (active_close){
           active_close.querySelector(".video_close_icon").click();
         } 
    }
  });
});
// ======================== Hide index page discount =========================
if( document.querySelector(".discount-code .close-icon")){
   document.querySelector(".discount-code .close-icon").addEventListener("click", function (e) {
        e.preventDefault();
       document.querySelector("body").classList.add("discount-hide");
    });
}
if( document.querySelector(".discount-code h4")){
   document.querySelector(".discount-code h4").addEventListener("click", function (e) {
        e.preventDefault();
       document.querySelector("body").classList.add("show_newletter_popup");
    });
}
if( document.querySelector(".newsletter_popup .close-icon")){
   document.querySelector(".newsletter_popup .close-icon").addEventListener("click", function (e) {
        e.preventDefault();
       document.querySelector("body").classList.remove("show_newletter_popup");
    });
}
if (window.location.href.includes("#popup_newsletter_signup") && !window.location.href.includes("challenge")) {
       document.querySelector("body").classList.add("show_newletter_popup");
}
if( document.querySelector(".newsletter_popup")){
   document.querySelector(".newsletter_popup").addEventListener("click", function (e) {     
         if (e.target === document.querySelector(".newsletter_popup")) {
             document.querySelector(".newsletter_popup .close-icon").click();
         } 
    });
}
const toggle_Button = document.querySelector('.sound-play-pause-button');
if(toggle_Button) {
         toggle_Button.addEventListener('click', function (e) {
            const video = this.closest(".video-section").querySelector("video");
         video.muted = !video.muted;
          if (video.muted) {
            this.classList.remove('volume-up');
            this.classList.add('volume-mute');
          } else {
            this.classList.remove('volume-mute');
            this.classList.add('volume-up');
          }
        });  
}



 document.addEventListener('DOMContentLoaded', function() {
  const videos = document.querySelectorAll(".gallery-image-inner video");
   if (videos) {
        videos.forEach((video) => {
        video.play().catch((e) => {
        console.warn("Autoplay failed for a video:", e);
        });
        });  
   }
   
    // Select the div with the class 'image-overlay-block'
    const imageOverlayBlock = document.querySelector('.image-overlay-block');

    if (imageOverlayBlock) {
        // Select all image elements within the 'image-overlay-block' div
        const images = imageOverlayBlock.querySelectorAll('img');

        images.forEach(image => {
            // Ensure the image has loaded to get accurate dimensions
            // and that it's rendered in the DOM
            if (image.complete && image.offsetHeight > 0) { // Check offsetHeight > 0 to ensure it's rendered
                applyMargin(image);
            } else {
                image.addEventListener('load', () => {
                    // Re-check offsetHeight after load as it might still be 0 if display: none; etc.
                    if (image.offsetHeight > 0) {
                        applyMargin(image);
                    } else {
                        console.warn(`Image loaded but has 0 offsetHeight: ${image.src}. Margin not applied.`);
                    }
                });
            }
        });
    }

    function applyMargin(image) {
        // Calculate the actual rendered height of the image
        const imageHeight = image.offsetHeight; // Use offsetHeight for actual rendered height

        // Calculate 50% of the image height for the margin
        const marginTopValue = - (imageHeight * 0.5);

        // Apply the margin-top style
        image.style.marginTop = `${marginTopValue}px`;
        console.log(`Image: ${image.src}, Actual Rendered Height: ${imageHeight}px, Applied Margin-Top: ${marginTopValue}px`);
    }
});



// quik view utl change 
  document.querySelectorAll("[quick_view_url]").forEach(function (link) {
    link.addEventListener("click", function (e) {
        e.preventDefault();
        // console.log("linkssss",link);
        const parent = link.closest('.product-block__detail');
        // console.log("parent",parent);
        const trigger = parent.querySelector('.quickbuy-toggle');
        trigger.href = link.href;
        trigger.click();
    });
  });


const section = document.querySelector(".template-suffix-recovery .video-section");
const video = section?.querySelector("video"); 

if (section && video) {
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        video.play();
      } else {
        video.pause();
      }
    });
  }, {
    threshold: 0.5 
  });

  observer.observe(section);
}

const optionlist = document.querySelectorAll('.product-block-options__item');

if (optionlist.length > 1) {
  optionlist.forEach(option => {
    option.addEventListener('mouseenter', function () {
      const newImg = this.getAttribute('data-img'); 
      const blockInner = this.closest('.block-inner'); 
      const activeImg = blockInner?.querySelector('.product-block__image--active img'); 

      if (activeImg && newImg) {
        activeImg.src = newImg;
        activeImg.srcset = newImg; // also update srcset
      }
    });
  });
}



const quick_buy = document.querySelectorAll(".influencer-overlay")
quick_buy.forEach(function (overlay) {
  overlay.addEventListener("click", function () {
    const containers = document.querySelectorAll(".quickbuy-container");
    containers.forEach(function (container) {
      let closeBtn = container.querySelector(".close-detail");
      console.log("closeBtn:--",closeBtn);
    if (closeBtn) {
      closeBtn.click();
    }
    });
    
  });
});



const quick_buy_new = document.querySelectorAll(".quick-overlay")
quick_buy_new.forEach(function (overlay) {
  overlay.addEventListener("click", function () {
    const containers = document.querySelectorAll(".quickbuy-container");
    containers.forEach(function (container) {
      let closeBtn = container.querySelector(".close-detail");
    if (closeBtn) {
      closeBtn.click();
    }
    });
    
  });
});




(function() {
const swiper_int = document.querySelector('.slider-show-videoplay')?.swiper;
if(swiper_int){
const activeSlideElement_new = swiper_int.slides[swiper_int.activeIndex]; 
  const activeVideo_new = activeSlideElement_new.querySelector('video');
  console.log("activeSlideElement_new",activeSlideElement_new)
   let videoInterval_new = null;
  if (activeVideo_new) {
    swiper_int.autoplay.stop();
  const videos = document.querySelectorAll('video');
  videos.forEach(video => {
    video.pause();
  });
    activeVideo_new.play();
       videoInterval_new = setInterval(() => {
        console.log("activeVideo.paused",activeVideo_new.paused);
      if (activeVideo_new.paused) {
         clearInterval(videoInterval_new);
         swiper_int.slideNext();
          swiper_int.autoplay.start();
      }
    }, 100);
  }

swiper_int.on('slideChange', function () {
   let videoInterval = null;
          swiper_int.autoplay.stop();
 const activeSlideElement = swiper_int.slides[swiper_int.activeIndex]; 
  const activeVideo = activeSlideElement.querySelector('video');
  // console.log(activeSlideElement)
  if (activeVideo) {
  const videos = document.querySelectorAll('video');
  videos.forEach(video => {
    video.pause();
  });
    activeVideo.play();
       videoInterval = setInterval(() => {
        // console.log("activeVideo.paused",activeVideo.paused);
      if (activeVideo.paused) {
         clearInterval(videoInterval);
         swiper_int.slideNext();
          swiper_int.autoplay.start();
      }
    }, 100);
  }else{
            swiper_int.autoplay.start();
  }
});


}})();

// !function(){const e=document.querySelector(".slider-show-videoplay")?.swiper;e.on("slideChange",function(){let t=null;e.autoplay.stop();const s=e.slides[e.activeIndex],i=s.querySelector("video");if(i){document.querySelectorAll("video").forEach(e=>{e.pause()}),i.play(),t=setInterval(()=>{i.paused&&(clearInterval(t),e.autoplay.start())},500)}else e.autoplay.start()})}();
